import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { InvitesHttpService } from "@/modules/candidate/services/InvitesHttpService";
import { isValidNumericParam } from "@/shared/utils/common.utils";
import { NOT_FOUND_ROUTE } from "@/routing/AppRouter/routes.constants";
import { notification } from "antd";
import { AssessmentInviteStatus } from "../scenes/AssessmentInvites/invites.constants";

export interface ICompositeInterviewQuestion {
  body: string;
  rank: number;
}

export interface ICompositeInterviewGuide {
  positiveIndicators: string[];
  negativeIndicators: string[];
  questions: ICompositeInterviewQuestion[];
}

export interface IAssessmentComposite {
  title: string;
  definition: string;
  finalScore: number;
  isOverall: boolean;
  rank: number;
  compositeKey: string;
  icon: string | null;
  interviewGuide?: ICompositeInterviewGuide;
  parentId?: number;
  subComposites?: IAssessmentComposite[];
}

export function useGetAssessmentCompositesByInviteHook({
  inviteId,
  hasTemplate,
  includeInterviewGuide,
  inviteStatus,
}: {
  inviteId: number;
  hasTemplate: boolean;
  includeInterviewGuide: boolean;
  inviteStatus?: AssessmentInviteStatus;
}) {
  const navigate = useNavigate();
  const [composites, setComposites] = useState<IAssessmentComposite[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const handleError = (message?: string) => {
      if (message)
        notification.error({
          message,
        });

      navigate(`/${NOT_FOUND_ROUTE}`);
    };

    const fetchComposites = async () => {
      if (!hasTemplate || inviteStatus !== AssessmentInviteStatus.Done) {
        setComposites([]);
        return;
      }

      if (!isValidNumericParam(inviteId)) {
        handleError("The assessment invite is not valid.");
        return;
      }

      try {
        setIsLoading(true);
        const data: IAssessmentComposite[] = await InvitesHttpService.getInviteCompositesWithScores(Number(inviteId), {
          includeInterviewGuide: includeInterviewGuide,
        });
        if (data.length === 0) {
          handleError("Could not find any competencies.");
          return;
        }
        setComposites(data);
      } catch {
        handleError("Failed to fetch competencies.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchComposites();
  }, [inviteId, hasTemplate, navigate, inviteStatus, includeInterviewGuide]);

  return { composites, isLoading };
}
